import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { itemTypesApi } from '@/api/itemTypesApi';
import type { ProductFormState } from './types';

interface StepItemTypeProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
  error?: string;
}

export function StepItemType({ state, updateState, error }: StepItemTypeProps) {
  const { data: itemTypes, isLoading } = useQuery({
    queryKey: ['item-types'],
    queryFn: () => itemTypesApi.getAll(false),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600 dark:text-warm-sand">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading item types...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-3">
          Product Type <span className="text-danger-400">*</span>
        </label>
        <p className="text-xs text-gray-600 dark:text-warm-sand/70 mb-4">
          What is the customer buying? Keychain, apparel, sign, magnet, and more.
        </p>
        {itemTypes?.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-warm-sand italic">
            No item types configured — add some in Catalog → Item Types.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {itemTypes?.map((item) => {
              const isSelected = state.itemTypeId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() =>
                    updateState((prev) => ({ ...prev, itemTypeId: item.id }))
                  }
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border-2
                    ${
                      isSelected
                        ? 'bg-amber-450/10 border-amber-450 shadow-md'
                        : 'bg-white dark:bg-charcoal-800 border-gray-200 dark:border-charcoal-700 hover:border-gray-300 dark:hover:border-charcoal-600'
                    }
                  `}
                >
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">
                    {item.icon || '📦'}
                  </span>
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        isSelected ? 'text-amber-450' : 'text-gray-900 dark:text-warm-cream'
                      }`}
                    >
                      {item.name}
                    </p>
                    {item.description && (
                      <p className="text-xs text-gray-600 dark:text-warm-sand/70 truncate">
                        {item.description}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="w-4 h-4 text-amber-450 flex-shrink-0 ml-auto" />
                  )}
                </button>
              );
            })}
          </div>
        )}
        {error && <p className="mt-2 text-sm text-danger-400">{error}</p>}
      </div>
    </div>
  );
}
